import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowRight, Clock, Lock, CheckCircle2, FileText, ShieldAlert } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BrandMark } from "@/components/BrandMark";
import { BookingSocialProof } from "@/components/BookingSocialProof";
import { SignInRequiredGate } from "@/components/SignInRequiredGate";

export default function Book() {
  useSEO({
    title: "Book a Live Virtual Boxing Session — The Flow State Method",
    description: "Book a private 40-minute live virtual boxing lesson with Coach Chris on Zoom. $70 per session. Secure Stripe checkout. Reschedule free 24h+ in advance.",
    path: "/book",
  });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const initialPkg = params.get("package") || "virtual_40";
  const initialRef = params.get("ref") || "";

  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState(initialPkg);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [referralCode, setReferralCode] = useState(initialRef);
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // { code, discount, final_amount }
  const [couponBusy, setCouponBusy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptWaiver, setAcceptWaiver] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/packages").then((r) => setPackages(r.data)); }, []);

  const pkg = packages.find((p) => p.id === packageId);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: couponCode.trim().toUpperCase(),
        package_id: packageId,
        email: email || user?.email || null,
      });
      setCouponInfo(data);
      toast.success(`Coupon applied — $${data.discount.toFixed(2)} off`);
    } catch (err) {
      setCouponInfo(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid coupon");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => { setCouponInfo(null); setCouponCode(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in or create an account to continue.");
      return;
    }
    if (!acceptTerms || !acceptWaiver) return toast.error("Please accept the Terms of Service and Liability Waiver");
    setLoading(true);
    try {
      const origin = window.location.origin;
      // Logged-in path: create booking + checkout in one go
      const { data: booking } = await api.post("/bookings", {
        package_id: packageId, notes,
        terms_accepted: acceptTerms, waiver_accepted: acceptWaiver,
      });
      const { data: ck } = await api.post("/payments/checkout/session", {
        package_id: packageId, origin_url: origin, booking_id: booking.id,
        coupon_code: couponInfo?.code || null,
      });
      window.location.href = ck.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="book-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-[#27272A]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="book-logo">
            <BrandMark size="md" />
          </Link>
          {!user && (
            <Link to={`/login?next=${encodeURIComponent("/book?package=" + packageId)}`} className="text-xs text-zinc-400 hover:text-white uppercase tracking-widest" data-testid="book-signin-link">
              Already have an account? Sign in
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Book a session</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-none mb-2">Reserve your spot.</h1>
        <p className="text-zinc-400 mb-8 max-w-xl">Pay first, schedule second. After checkout, Coach Chris reaches out within 24 hours to lock in your session time. {user ? "You're signed in — we'll add this to your portal." : "Your account is created at checkout — no extra steps."}</p>

        <BookingSocialProof />

        {/* Booking policy notice */}
        <div className="mb-10 border border-[#FF331F]/40 bg-[#FF331F]/5 p-5 text-sm text-zinc-300" data-testid="booking-policy-notice">
          <div className="font-display uppercase tracking-widest text-[#FF331F] text-xs mb-2">How it Works</div>
          <ul className="space-y-1.5 leading-relaxed">
            <li>• <strong className="text-white">Step 1:</strong> Pay below to reserve your session.</li>
            <li>• <strong className="text-white">Step 2:</strong> Coach Chris contacts you <span className="text-white">within 24 hours</span> (phone, text, or email) to coordinate your Zoom session time.</li>
            <li>• <strong className="text-white">Step 3:</strong> Once your time is locked in, you&apos;ll receive the Zoom link and a confirmation in your client portal.</li>
            <li>• All sessions are conducted in <strong className="text-white">English only</strong>.</li>
            <li>• <strong className="text-white">All payments are non-refundable.</strong> No cash refunds for cancelled or unused sessions.</li>
            <li>• Cancel <strong className="text-white">24+ hours before</strong> a confirmed session to reschedule at no cost.</li>
            <li>• Cancellations under 24 hours, no-shows, or arriving more than 10 min late forfeit the session.</li>
            <li>• Coach-initiated cancellations are rescheduled at no cost.</li>
          </ul>
          <p className="text-[11px] text-zinc-500 mt-3">By continuing you confirm you understand and accept this policy and the full <a href="/terms" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">Terms of Service</a>.</p>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-5 gap-8">
          {/* Left: Form */}
          <div className="lg:col-span-3 space-y-6">
            <section className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-4" data-testid="book-step-1">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 grid place-items-center bg-[#FF331F] text-white font-display font-bold">1</div>
                <div className="font-display uppercase text-lg tracking-widest">Session</div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Package</Label>
                {packages.length > 1 ? (
                  <div className="space-y-2">
                    {packages.map((p) => (
                      <button
                        key={p.id} type="button" onClick={() => setPackageId(p.id)}
                        className={`w-full text-left border rounded-sm p-4 transition-colors ${packageId === p.id ? "border-[#FF331F] bg-[#FF331F]/10" : "border-[#27272A] bg-[#050505] hover:border-zinc-600"}`}
                        data-testid={`book-package-option-${p.id}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-display uppercase text-sm">{p.name}</div>
                            {p.description && <div className="text-[11px] text-zinc-500 mt-1">{p.description}</div>}
                            {p.is_alumni_rate && <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-1">Alumni rate unlocked</div>}
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="font-display text-2xl text-white">${p.amount}</div>
                            {p.alumni_amount && !p.is_alumni_rate && (
                              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Alumni: ${p.alumni_amount}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : pkg ? (
                  <div className="bg-[#050505] border border-[#27272A] rounded-sm p-4 flex items-center justify-between" data-testid="book-package-display">
                    <div>
                      <div className="font-display uppercase text-sm">{pkg.name}</div>
                      <div className="text-[11px] text-zinc-500 uppercase tracking-widest mt-1">{pkg.duration} min · Live via Zoom</div>
                    </div>
                    <div className="font-display text-2xl text-white">${pkg.amount}</div>
                  </div>
                ) : (
                  <div className="bg-[#050505] border border-[#27272A] rounded-sm p-4 text-sm text-zinc-500">Loading…</div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">Your availability & notes for the coach</Label>
                <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell Coach Chris when you're generally free so he can pick the best time to call. (e.g. weekday evenings after 7pm EST, Saturday mornings). Also: goals, experience level, equipment available, time zone, anything else." className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="book-notes" />
              </div>
            </section>

            <section className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-4" data-testid="book-step-2">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 grid place-items-center bg-[#FF331F] text-white font-display font-bold">2</div>
                <div className="font-display uppercase text-lg tracking-widest">{user ? "Your info" : "Your account"}</div>
              </div>
              {user ? (
                <div className="text-sm text-zinc-300">
                  Signed in as <span className="text-white font-display uppercase">{user.name}</span> · {user.email}
                </div>
              ) : (
                <SignInRequiredGate
                  title="Sign in to book your session"
                  subtitle="A free account lets you view session history, reschedule, and access your Zoom link in one place."
                  testid="book-signin-gate"
                />
              )}
            </section>

            <section className="border border-[#27272A] bg-[#0F0F0F] p-6 space-y-4" data-testid="book-step-3">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 grid place-items-center bg-[#FF331F] text-white font-display font-bold">3</div>
                <div className="font-display uppercase text-lg tracking-widest">Agreements</div>
              </div>
              <p className="text-zinc-400 text-sm">Boxing involves real physical risk — please read both documents before booking.</p>

              <label className="flex items-start gap-3 p-4 border border-[#27272A] bg-[#050505] hover:border-[#FF331F]/50 cursor-pointer transition-colors" data-testid="terms-checkbox-label">
                <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#FF331F] data-[state=checked]:border-[#FF331F]" data-testid="accept-terms-checkbox" />
                <div className="flex-1 text-sm">
                  <div className="flex items-center gap-2 text-white font-display uppercase tracking-widest text-xs"><FileText className="h-3.5 w-3.5 text-[#FF331F]" /> I accept the Terms of Service</div>
                  <div className="text-zinc-500 text-xs mt-1">
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline" data-testid="open-terms-link">Read the full Terms of Service →</a>
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 border border-[#27272A] bg-[#050505] hover:border-[#FF331F]/50 cursor-pointer transition-colors" data-testid="waiver-checkbox-label">
                <Checkbox checked={acceptWaiver} onCheckedChange={(v) => setAcceptWaiver(!!v)} className="mt-0.5 border-zinc-600 data-[state=checked]:bg-[#FF331F] data-[state=checked]:border-[#FF331F]" data-testid="accept-waiver-checkbox" />
                <div className="flex-1 text-sm">
                  <div className="flex items-center gap-2 text-white font-display uppercase tracking-widest text-xs"><ShieldAlert className="h-3.5 w-3.5 text-[#FF331F]" /> I accept the Liability Waiver & Release</div>
                  <div className="text-zinc-500 text-xs mt-1">
                    I acknowledge the inherent risks of boxing training, assume those risks, and release The Flow State Method LLC from liability for injuries arising from ordinary negligence. <a href="/waiver" target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline" data-testid="open-waiver-link">Read the full Waiver →</a>
                  </div>
                </div>
              </label>

              <p className="text-[11px] text-zinc-500">By checking these boxes you provide your electronic signature. Acceptance timestamp, IP, and browser are recorded with your booking.</p>
            </section>
          </div>

          {/* Right: Summary */}
          <aside className="lg:col-span-2">
            <div className="sticky top-24 border border-[#FF331F]/50 bg-[#0F0F0F] p-6 space-y-5" data-testid="book-summary">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Order Summary</div>
              {pkg && (
                <>
                  <div>
                    <div className="font-display uppercase text-xl leading-tight">{pkg.name}</div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest mt-2">
                      <Clock className="h-3 w-3" /> {pkg.duration} minutes · {pkg.type.replace("_", " ")}
                    </div>
                  </div>

                  {/* Coupon */}
                  <div className="border-t border-[#27272A] pt-4 space-y-2">
                    <Label className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Coupon / Offer</Label>
                    {couponInfo ? (
                      <div className="flex items-center justify-between bg-[#FF331F]/10 border border-[#FF331F]/40 px-3 py-2" data-testid="coupon-applied">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">{couponInfo.code}</div>
                          <div className="text-xs text-zinc-300">−${couponInfo.discount.toFixed(2)} applied</div>
                        </div>
                        <button type="button" onClick={removeCoupon} className="text-xs text-zinc-400 hover:text-white uppercase tracking-widest" data-testid="remove-coupon-btn">Remove</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Enter code" maxLength={20} className="bg-[#050505] border-[#27272A] rounded-sm h-10 uppercase text-xs" data-testid="coupon-code-field" />
                        <Button type="button" onClick={applyCoupon} disabled={couponBusy || !couponCode.trim()} variant="outline" className="border-[#27272A] text-white hover:bg-[#FF331F] hover:border-[#FF331F] uppercase tracking-widest text-xs font-bold rounded-sm bg-transparent" data-testid="apply-coupon-btn">
                          {couponBusy ? "…" : "Apply"}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-[#27272A] pt-4 space-y-2 text-sm">
                    <div className="flex justify-between text-zinc-400">
                      <span>Session</span>
                      <span>${pkg.amount.toFixed(2)}</span>
                    </div>
                    {couponInfo && (
                      <div className="flex justify-between text-[#FF331F]">
                        <span>Coupon ({couponInfo.code})</span>
                        <span>−${couponInfo.discount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-zinc-500 text-xs">
                      <span>Stripe processing</span>
                      <span>Included</span>
                    </div>
                  </div>
                  <div className="border-t border-[#27272A] pt-4 flex justify-between items-end">
                    <span className="font-display uppercase tracking-widest text-sm">Total</span>
                    <span className="font-display text-4xl text-white">${(couponInfo ? couponInfo.final_amount : pkg.amount).toFixed(2)}</span>
                  </div>
                </>
              )}
              <Button type="submit" disabled={loading || !user || !acceptTerms || !acceptWaiver} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6 disabled:bg-zinc-700 disabled:cursor-not-allowed" data-testid="book-submit-btn">
                {loading ? "Redirecting to Stripe…" : !user ? <>Sign in to continue</> : <>Continue to payment <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
              {!user && (
                <p className="text-[11px] text-zinc-500 text-center -mt-2" data-testid="signin-required-hint">Sign in or create an account above to continue</p>
              )}
              {user && (!acceptTerms || !acceptWaiver) && (
                <p className="text-[11px] text-zinc-500 text-center -mt-2" data-testid="acceptance-required-hint">Accept the Terms & Waiver to continue</p>
              )}
              <ul className="space-y-2 pt-2">
                <li className="flex items-start gap-2 text-xs text-zinc-400"><CheckCircle2 className="h-3.5 w-3.5 text-[#FF331F] flex-shrink-0 mt-0.5" /> Secure payment via Stripe</li>
                <li className="flex items-start gap-2 text-xs text-zinc-400"><CheckCircle2 className="h-3.5 w-3.5 text-[#FF331F] flex-shrink-0 mt-0.5" /> Coach contacts you within 24 hours to schedule</li>
                <li className="flex items-start gap-2 text-xs text-zinc-400"><CheckCircle2 className="h-3.5 w-3.5 text-[#FF331F] flex-shrink-0 mt-0.5" /> Reschedule free 24h+ before session</li>
                <li className="flex items-start gap-2 text-xs text-zinc-400"><CheckCircle2 className="h-3.5 w-3.5 text-[#FF331F] flex-shrink-0 mt-0.5" /> Zoom link sent once session time is confirmed</li>
                <li className="flex items-start gap-2 text-[11px] text-zinc-500"><span className="h-3.5 w-3.5 flex-shrink-0" /> All sessions conducted in English. Non-refundable.</li>
              </ul>
            </div>
          </aside>
        </form>
      </main>
    </div>
  );
}
